import { addHours } from "date-fns";
import {
  BookingSchedule,
  BookingScheduleDate,
  BookingScheduleDateTime,
  BookingScheduleDay,
  BookingScheduleTime,
  BookingScheduleValidationOptions,
} from "@/lib/types/schedule-types";
import { TimeData } from "@/lib/utils/time-utils";

export const defaultScheduleDay: BookingScheduleDay = {
  start: {
    date: null,
    time: null,
  },
  end: {
    date: null,
    time: null,
  },
};

/**
 * Updates booking schedule to normalize time data, ensuring time_index is always present
 * @param schedule - BookingSchedule
 * @returns BookingSchedule with normalized time data
 */
const normalizeBookingSchedule = (
  schedule: BookingSchedule,
): BookingSchedule => {
  if (!schedule) {
    return [];
  }

  return schedule.map((day) => {
    const startTimeObj = TimeData.find(
      (timeObj) =>
        timeObj.h === day.start?.time?.h && timeObj.m === day.start?.time?.m,
    );
    const start = {
      ...day.start,
      time: {
        ...(day.start?.time || {}),
        ...(startTimeObj || {}),
      } as BookingScheduleTime,
    };

    const endTimeObj = TimeData.find((timeObj) => {
      const isNextDay =
        day.start?.date?.d &&
        day.end?.date?.d &&
        (day.end?.date?.d > day.start?.date?.d ||
          day.end?.date?.m > day.start?.date?.m);
      if (isNextDay) {
        return (
          timeObj.overnight &&
          timeObj.h === day.end?.time?.h &&
          timeObj.m === day.end?.time?.m
        );
      }
      return timeObj.h === day.end?.time?.h && timeObj.m === day.end?.time?.m;
    });
    const end = {
      ...day.end,
      time: {
        ...(day.end?.time || {}),
        ...(endTimeObj || {}),
      } as BookingScheduleTime,
    };

    return { start, end };
  });
};

/**
 * Converts a Date object to a BookingScheduleDateTime object
 * @param date - Date object
 * @returns BookingScheduleDateTime object
 */
const dateToBookingScheduleDateTime = (
  date: Date,
): BookingScheduleDateTime | null => {
  if (!date) {
    return null;
  }

  return {
    date: {
      y: date.getFullYear(),
      m: date.getMonth() + 1,
      d: date.getDate(),
    },
    time: {
      h: date.getHours(),
      m: date.getMinutes(),
    },
  } as BookingScheduleDateTime;
};

/**
 * Validates a BookingScheduleDate object
 * @param date - BookingScheduleDate object
 * @param allowPastDates - Whether to allow past dates
 * @returns true if the date is valid, false otherwise
 */
const validateScheduleDate = (
  date: BookingScheduleDate | null,
  allowPastDates = false,
): boolean => {
  if (!date?.y || !date?.m || !date?.d) {
    return false;
  }

  if (allowPastDates) {
    return true;
  }

  const scheduleDate = new Date(date.y, date.m - 1, date.d);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day (00:00:00)

  const isPastDate = scheduleDate < today;
  return !isPastDate;
};

/**
 * Validates a BookingScheduleTime object
 * @param time - BookingScheduleTime object
 * @returns true if the time is valid, false otherwise
 */
const validateScheduleTime = (time: BookingScheduleTime | null): boolean => {
  if ((!time?.h && time?.h !== 0) || (!time?.m && time?.m !== 0)) {
    return false;
  }

  return true;
};

/**
 * Converts a BookingScheduleDateTime object to a Date object
 * @param bookingScheduleDate - BookingScheduleDateTime object with optional time
 * @returns Date object
 */
const bookingScheduleDateTimeToDate = (
  bookingScheduleDate: Omit<BookingScheduleDateTime, "time"> & {
    time?: BookingScheduleTime | null;
  },
  allowPastDates = true,
): Date | null => {
  if (!validateScheduleDate(bookingScheduleDate?.date, allowPastDates)) {
    return null;
  }

  const date = bookingScheduleDate.date!;
  const time = bookingScheduleDate.time;

  return new Date(date.y, date.m - 1, date.d, time?.h || 0, time?.m || 0);
};

/**
 * Fixes the duration of a BookingScheduleDay
 * @param day - BookingScheduleDay
 * @param duration - The duration to set the end time to
 * @returns The fixed BookingScheduleDay with the new end time
 */
const fixInvalidScheduleDuration = (
  day: BookingScheduleDay,
  duration: number,
): BookingScheduleDay => {
  const startDate = bookingScheduleDateTimeToDate(day.start);
  const newEndDate = dateToBookingScheduleDateTime(
    addHours(startDate!, duration),
  )!;

  return normalizeBookingSchedule([
    {
      ...day,
      end: {
        ...newEndDate!,
        time: {
          ...newEndDate.time,
          available: true,
        } as BookingScheduleTime,
      },
    },
  ])[0];
};

/**
 * Validates a BookingScheduleDay and fixes invalid durations if fixInvalidDuration is true
 * @param day - BookingScheduleDay
 * @param options - ValidateScheduleDayOptions
 * @returns The validated BookingScheduleDay or null if invalid
 */
const validateScheduleDay = (
  day: BookingScheduleDay,
  options?: BookingScheduleValidationOptions,
): {
  scheduleDay: BookingScheduleDay | null;
  modificationType?: "exactDuration" | "minDuration";
} => {
  const {
    allowPastDates = false,
    minDuration = 0,
    exactDuration = 0,
    fixInvalidDuration = false,
  } = options || {};

  if (!day) {
    return {
      scheduleDay: null,
    };
  }

  const { start, end } = day || {};

  if (
    !validateScheduleDate(start?.date, allowPastDates) ||
    !validateScheduleTime(start?.time) ||
    !validateScheduleDate(end?.date, allowPastDates) ||
    !validateScheduleTime(end?.time)
  ) {
    return {
      scheduleDay: null,
    };
  }

  const startDate = new Date(
    start.date!.y,
    start.date!.m - 1,
    start.date!.d,
    start.time!.h,
    start.time!.m,
  );

  const endDate = new Date(
    end.date!.y,
    end.date!.m - 1,
    end.date!.d,
    end.time!.h,
    end.time!.m,
  );

  // If the start date is after the end date, the schedule is invalid
  if (startDate >= endDate) {
    return {
      scheduleDay: null,
    };
  }

  let validScheduleDay: BookingScheduleDay | null = day;
  let modificationType: "exactDuration" | "minDuration" | undefined = undefined;

  if (exactDuration > 0) {
    // If exactDuration is set, check if the duration matches exactly
    const hoursDiff =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    if (hoursDiff !== exactDuration) {
      if (fixInvalidDuration) {
        validScheduleDay = fixInvalidScheduleDuration(day, exactDuration);
        modificationType = "exactDuration";
      } else {
        validScheduleDay = null;
      }
    }
  } else if (minDuration > 0) {
    // If minDuration is set, check if the duration meets the minimum
    const hoursDiff =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    if (hoursDiff < minDuration) {
      if (fixInvalidDuration) {
        validScheduleDay = fixInvalidScheduleDuration(day, minDuration);
        modificationType = "minDuration";
      } else {
        validScheduleDay = null;
      }
    }
  }

  return {
    scheduleDay: validScheduleDay,
    modificationType,
  };
};

/**
 * Creates a valid booking schedule by filtering out invalid days or fixing invalid durations
 * @param bookingSchedule - BookingSchedule
 * @param options - BookingScheduleValidationOptions
 * @returns BookingSchedule
 */
const createValidBookingSchedule = (
  bookingSchedule: BookingSchedule,
  options?: BookingScheduleValidationOptions,
): BookingSchedule => {
  if (!Array.isArray(bookingSchedule)) {
    return [];
  }

  const normalizedSchedule = normalizeBookingSchedule(bookingSchedule);
  const validSchedule: BookingSchedule = [];
  let modificationType: "exactDuration" | "minDuration" | undefined = undefined;

  normalizedSchedule.forEach((day) => {
    const validDay = validateScheduleDay(day, options);
    if (validDay.scheduleDay) {
      validSchedule.push(validDay.scheduleDay);
      modificationType = modificationType || validDay.modificationType;
    }
  });

  if (modificationType) {
    options?.onAutoCorrect?.(modificationType);
  }

  return validSchedule;
};

export {
  bookingScheduleDateTimeToDate,
  createValidBookingSchedule,
  dateToBookingScheduleDateTime,
  fixInvalidScheduleDuration,
  normalizeBookingSchedule,
  validateScheduleDate,
  validateScheduleDay,
  validateScheduleTime,
};
